/**
 * One leg in the builder — ROADMAP 5.23.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SIDE AND TYPE ARE TOGGLES, NOT DROPDOWNS
 *
 * Both are two-valued and both are the first thing read when checking a
 * structure. A dropdown hides one of the two answers behind a click and
 * renders the other as prose; a pair of buttons shows the choice and the
 * alternative at once, in the colour the chips already use — so a four-leg
 * condor can be verified by looking down the column rather than by reading
 * it, and setting one costs a single click instead of two.
 *
 * COPY BEFORE ADD
 *
 * Most second legs are the first leg with one field changed: the other side
 * of a strangle, the far wing of a condor. Copy carries every field and the
 * row that lands is one edit from being right, where Add starts blank and
 * needs five. It is the difference between four clicks and twenty for an
 * iron condor.
 *
 * DELETE ASKS FIRST
 *
 * In place, as a two-step on the same button rather than a modal: a dialog
 * over a dialog is a worse interruption than a button that changes its mind,
 * and the row stays visible behind the question so the trader can see which
 * one they are about to lose.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState } from 'react'
import { Copy, Trash2 } from 'lucide-react'
import { EXPIRY_KINDS, LEG_ROLES, LEG_ROLE_LABEL, type ExpiryKind, type LegRole } from '@apna/shared'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  acceptNumeric,
  EXPIRY_CHOICE_LABEL,
  MODE_SHAPE,
  numericKindOf,
  STRIKE_MODES,
  STRIKE_MODE_LABEL,
  type LegDraft,
  type StrikeMode,
} from '../utils/legDraft'

/*
 * One definition, shared by the rows and the labels above them. The free
 * space goes to STRIKE because its options carry the longest text; every
 * other column is sized to its content, so a number never sits in the middle
 * of an empty field.
 */
export const LEG_GRID =
  'grid grid-cols-[16px_88px_62px_112px_minmax(136px,1fr)_168px_56px_96px_52px] items-center gap-1.5'

const TRIGGER = 'h-8 w-full min-w-0 px-2 text-[12px]'

function Toggle<T extends string>({
  value,
  options,
  onChange,
  tone,
  label,
}: {
  value: T
  options: readonly { value: T; label: string }[]
  onChange: (v: T) => void
  tone: (v: T) => string
  label: string
}) {
  return (
    <div
      className="flex h-8 overflow-hidden rounded-md border border-input"
      role="group"
      aria-label={label}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={cn(
            'flex-1 text-[11.5px] font-semibold transition-colors',
            value === o.value ? tone(o.value) : 'text-muted-foreground hover:bg-accent',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function LegRow({
  draft: d,
  index,
  invalid,
  duplicate,
  canRemove,
  canCopy,
  onChange,
  onCopy,
  onRemove,
}: {
  draft: LegDraft
  index: number
  invalid: boolean
  duplicate: boolean
  canRemove: boolean
  canCopy: boolean
  onChange: (patch: Partial<LegDraft>) => void
  onCopy: () => void
  onRemove: () => void
}) {
  const shape = MODE_SHAPE[d.mode]
  const kind = numericKindOf(d.mode)
  const [confirming, setConfirming] = useState(false)

  /*
   * The box refuses the keystroke rather than accepting it and complaining
   * later — "OTM −10" is the case this exists for. See `acceptNumeric`.
   */
  const typed = (field: 'a' | 'b' | 'lots', raw: string) =>
    onChange({
      [field]: acceptNumeric(field === 'lots' ? 'COUNT' : kind, raw, d[field]),
    } as Partial<LegDraft>)

  return (
    <div
      className={cn(
        LEG_GRID,
        'rounded-[8px] border px-1.5 py-1.5 transition-colors',
        invalid
          ? 'border-destructive/60 bg-destructive/5'
          : confirming
            ? 'border-destructive/40 bg-destructive/[0.04]'
            : duplicate
              ? 'border-warning/50 bg-warning/5'
              : 'border-transparent hover:bg-accent/40',
      )}
      data-testid={`leg-row-${index}`}
    >
      <span className="num text-[11px] text-muted-foreground">{index + 1}</span>

      <Toggle
        label="Buy or sell"
        value={d.side}
        onChange={(side) => onChange({ side })}
        options={[
          { value: 'BUY' as const, label: 'Buy' },
          { value: 'SELL' as const, label: 'Sell' },
        ]}
        tone={(v) =>
          v === 'BUY' ? 'bg-success/15 text-success' : 'bg-destructive/15 text-destructive'
        }
      />

      <Toggle
        label="Call or put"
        value={d.optionType}
        onChange={(optionType) => onChange({ optionType })}
        options={[
          { value: 'CE' as const, label: 'CE' },
          { value: 'PE' as const, label: 'PE' },
        ]}
        tone={() => 'bg-primary/15 text-primary'}
      />

      <Select
        value={d.expiryKind === '' ? '@strategy' : d.expiryKind}
        onValueChange={(v) =>
          onChange({ expiryKind: v === '@strategy' ? '' : (v as ExpiryKind) })
        }
      >
        {/*
          An empty string is not a usable Select value — Radix reserves it for
          "nothing chosen" and clears the trigger — so "the strategy's expiry"
          travels as a sentinel and is mapped back on the way out.
        */}
        <SelectTrigger className={cn(TRIGGER, 'num')} data-testid={`leg-expiry-${index}`} aria-label="Expiry">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="@strategy">{EXPIRY_CHOICE_LABEL['']}</SelectItem>
          {EXPIRY_KINDS.map((k) => (
            <SelectItem key={k} value={k}>
              {EXPIRY_CHOICE_LABEL[k] ?? k}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={d.mode}
        onValueChange={(v) => onChange({ mode: v as StrikeMode, a: '', b: '' })}
      >
        <SelectTrigger
          className={TRIGGER}
          data-testid={`leg-mode-${index}`}
          aria-label="How this leg picks its strike"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STRIKE_MODES.map((m) => (
            <SelectItem key={m} value={m}>
              {STRIKE_MODE_LABEL[m]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* The boxes a mode needs, and nothing where it needs none. */}
      <div className="flex min-w-0 items-center gap-1">
        {shape.fields === 0 ? (
          <span className="pl-1 text-[11.5px] text-muted-foreground">—</span>
        ) : (
          <>
            <Input
              className="num h-8 min-w-0 flex-1 px-2 text-[12px]"
              value={d.a}
              onChange={(e) => typed('a', e.target.value)}
              placeholder={shape.placeholder[0]}
              inputMode="decimal"
              data-testid={`leg-a-${index}`}
              aria-label={shape.fields === 2 ? 'Lowest' : STRIKE_MODE_LABEL[d.mode]}
            />
            {shape.fields === 2 && (
              <>
                <span className="shrink-0 text-[10.5px] text-muted-foreground">to</span>
                <Input
                  className="num h-8 min-w-0 flex-1 px-2 text-[12px]"
                  value={d.b}
                  onChange={(e) => typed('b', e.target.value)}
                  placeholder={shape.placeholder[1]}
                  inputMode="decimal"
                  data-testid={`leg-b-${index}`}
                  aria-label="Highest"
                />
              </>
            )}
            {shape.unit && (
              <span className="shrink-0 text-[10px] text-muted-foreground">{shape.unit}</span>
            )}
          </>
        )}
      </div>

      <Input
        className="num h-8 px-1 text-center text-[12px]"
        value={d.lots}
        onChange={(e) => typed('lots', e.target.value)}
        inputMode="numeric"
        data-testid={`leg-lots-${index}`}
        aria-label="Lots on this leg"
      />

      <Select
        value={d.role === '' ? '@none' : d.role}
        onValueChange={(v) => onChange({ role: v === '@none' ? '' : (v as LegRole) })}
      >
        <SelectTrigger
          className={TRIGGER}
          data-testid={`leg-role-${index}`}
          aria-label="What this leg is for"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="@none">No role</SelectItem>
          {LEG_ROLES.map((r) => (
            <SelectItem key={r} value={r}>
              {LEG_ROLE_LABEL[r]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex items-center justify-end gap-0.5">
        <button
          type="button"
          onClick={onCopy}
          disabled={!canCopy}
          aria-label={`Copy leg ${index + 1}`}
          title="Copy this leg"
          data-testid={`leg-copy-${index}`}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary disabled:pointer-events-none disabled:opacity-30"
        >
          <Copy size={13} />
        </button>
        <button
          type="button"
          onClick={() => (confirming ? onRemove() : setConfirming(true))}
          onBlur={() => setConfirming(false)}
          disabled={!canRemove}
          aria-label={confirming ? `Confirm removing leg ${index + 1}` : `Remove leg ${index + 1}`}
          title={confirming ? 'Click again to remove' : 'Remove this leg'}
          data-testid={`leg-remove-${index}`}
          className={cn(
            'rounded p-1 transition-colors disabled:pointer-events-none disabled:opacity-30',
            confirming
              ? 'bg-destructive/15 text-destructive'
              : 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive',
          )}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  )
}

/** The labels above the rows. Separate so one grid definition serves both. */
export function LegHeader() {
  return (
    <div className={cn(LEG_GRID, 'px-1.5 pb-1')}>
      <span />
      {['Side', 'Type', 'Expiry', 'Strike', '', 'Lots', 'Role', ''].map((h, i) => (
        <span
          key={i}
          className="text-[9.5px] font-medium uppercase tracking-[0.07em] text-muted-foreground/70"
        >
          {h}
        </span>
      ))}
    </div>
  )
}
